import {createAsyncThunk} from "@reduxjs/toolkit";
import {prepareJsonPayload, getAuthHeader} from "../store/utils";
import {HOSTNAME} from "../configs/Domain";
import {deepObjectFromDotPath} from "../utils/tools";


async function batchFetchPortfolios(userId, portfolioIds) {
  try {
    const response = await fetch(`${HOSTNAME}/portfolios/list?ids=${portfolioIds.join(',')}`);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const portfolios = await response.json(); // Assuming the response body will be the list of portfolio objects
      console.log(portfolios)
    return portfolios;
  } catch (error) {
    console.error('Failed to fetch portfolios in batches:', error);
    throw error; // Re-throw the error to be handled by the caller
  }
}



export const fetchPortfoliosInBatches = createAsyncThunk(
  'portfolios/fetchInBatches',

    /**
     *
     * @param userId {uuid4} - The user ID
     * @param portfolioIds {Array[uuid4]} Array of portfolio uuids
     * @param userObjects {Array[object]} Array of objects from the user document portfolios_v2 array. This contains
     *                                    the user specific stuff for each portfolio such as if its starred
     * @param batchSize {number} - The number of portfolios to fetch in each batch
     * @param rejectWithValue {function} - The Redux store's rejectWithValue function
     */
  async ({ userId, portfolioIds, userObjects, batchSize = 50 }, { rejectWithValue }) => {
    try {
      let fetchedPortfolios = [];
      for (let i = 0; i < portfolioIds.length; i += batchSize) {
        const batchIds = portfolioIds.slice(i, i + batchSize);
        const response = await batchFetchPortfolios(userId, batchIds); // Implement this function to hit your /portfolios/list endpoint
        fetchedPortfolios = [...fetchedPortfolios, ...response];
      }
      // Use find to match user-specific details for each portfolio
      fetchedPortfolios = fetchedPortfolios.map(portfolio => {
        const userDetails = userObjects.find(obj => obj.id === portfolio.id) || {};
        return { ...portfolio, _user: userDetails};
      });
      return fetchedPortfolios;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


/**
 * Fetches the detailed data for a list of portfolios.
 *
 * --Note: Handles checking for existing data in the state and only fetching if needed
 */
export const fetchDetailedPortfolioObjects = createAsyncThunk(
  'portfolios/fetchDetailed',
  async (portfolioIds, { getState, rejectWithValue }) => {
    const state = getState();
    // Assuming `items` is an array, we need to filter based on the `detailsFetched` flag or its absence.
    const items = [...state.portfolios.items];
    const idsToFetch = portfolioIds.filter(id =>
      !items.some(item => item.id === id && item.detailsFetched && item.hasOwnProperty('symbols')));
    // Early return if all requested details are already fetched
    if (idsToFetch.length === 0) {
      return items.filter(item => portfolioIds.includes(item.id));
    }
    try {
      const queryString = idsToFetch.join(',');
      const response = await fetch(`${HOSTNAME}/portfolios/list/positions?ids=${queryString}`);
      if (!response.ok) throw new Error('Network response was not ok');
      const fetchedData = await response.json();
      return [...items, ...fetchedData.map(data => ({ ...data, detailsFetched: true }))];
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);


/**
 * Update the document form detail data i.e description/name/tags for a portfolio
 * !! Does NOT update the positions, use updatePortfolioPositions for that !!
 */
export const updatePortfolioDetails = createAsyncThunk(
  'portfolios/updatePortfolioDetails',
  async ({ portfolioId, data }, { rejectWithValue }) => {
      const response = await fetch(
          `${HOSTNAME}/portfolios/${portfolioId}/update/details`, prepareJsonPayload({data})
      );
      if (!response.ok) {
        throw new Error('Failed to update portfolio');
      }
      const responseData = await response.json();
      return { id: portfolioId, data: deepObjectFromDotPath(data) }
  }
);


/**
 *
 */
export const updateUserPortfolioData = createAsyncThunk(
  'portfolios/updateUserPortfolioData',
  async ({portfolioID, updateFields }, { rejectWithValue }) => {
    try {
      // Construct the request payload to generalize the update operation
      let update = {};
      for (let k in updateFields) {
        update[`portfolios_v2.$.${k}`] = updateFields[k];
      }
      const payload = {
        match: {
            uid: localStorage.getItem('uid'),
            'portfolios_v2.id': portfolioID
          }, // Matching the specific portfolio by ID
          update: {
            $set: update, // The update to apply
        }
      }
      const response = await fetch(`${HOSTNAME}/user/update`, prepareJsonPayload({data: payload}));
      if (!response.ok) throw new Error('Failed to update user portfolio data');
      const updatedData = await response.json();
      return updatedData;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const saveUserPortfolioSnapshot = createAsyncThunk(
    'portfolios/saveUserPortfolioSnapshot',
    async ({portfolioID, snapshot }, { rejectWithValue }) => {
      try {
        const response = await fetch(`${HOSTNAME}/user/signals/${portfolioID}/confirm`,
            prepareJsonPayload({
              data: {
                snapshot: snapshot
              }
            })
        )
        if (!response.ok) throw new Error('Failed to save user portfolio snapshot');
        const updatedData = await response.json();
        return {portfolioID, ...updatedData}
        } catch (error) {
        return rejectWithValue(error.toString());

      }
    }
);


export const createNewPortfolio = createAsyncThunk(
    'portfolios/createNewPortfolio',
    async ({title, type, itemArray, props}, { rejectWithValue }) => {
        try {
            const payload = {
                    name: title,
                    type: type,
                    ptfl: itemArray
            }
            if (props){
                payload.properties = props;
            }
            const response = await fetch(`${HOSTNAME}/portfolios/create`, prepareJsonPayload({
                data: payload
            }))
            if (!response.ok) throw new Error('Failed to create new portfolio');
            const data = await response.json();
            return data;
        }catch(e){

        }
    }
)

/**
 * Update the postions (holdings contents whatever) of a portfolio here
 */
export const updatePortfolioPositions = createAsyncThunk(
    'portfolios/updatePortfolioPositions',
    async ({portfolioId, itemArray}, { rejectWithValue }) => {
        try {
            const response = await fetch(`${HOSTNAME}/portfolios/${portfolioId}/update/positions`,
                prepareJsonPayload({
                data: {
                    ptfl: itemArray
                }}))
            if (!response.ok) throw new Error('Failed to create new portfolio');
            const data = await response.json();
            return data;
        }catch(e){

        }
    }
)

/**
 *
 */
export const deletePortfolios = createAsyncThunk(
    'portfolios/deletePortfolios',
    async ({portfolioIds}, { rejectWithValue }) => {
        try {
            const response = await fetch(`${HOSTNAME}/portfolios/delete`,
                prepareJsonPayload({
                    method: "DELETE",
                    data: {
                        ids: portfolioIds
                    }
                })
            )
            if (!response.ok) throw new Error('Failed to create new portfolio');
            const data = await response.json();
            return data;
        }catch(e){

        }
    }
)


/**
 * Fetches the history of a user's portfolio.
 * @param {Object} args - The arguments for the thunk.
 * @param {string} args.portfolioID - The ID of the portfolio to fetch history for.
 * @returns {Promise<Object>} The portfolio history data.
 */
export const fetchUserPortfolioHistory = createAsyncThunk(
  'portfolios/fetchUserPortfolioHistory',
  async ({ portfolioId, limit=-1 }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${HOSTNAME}/portfolios/${portfolioId}/user/history?limit=${limit}`, {
        method: 'GET',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch user portfolio history');
      const historyData = await response.json();
      return {portfolioId, historyData};
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

